import { useEffect, useId, useLayoutEffect, useRef, type MouseEvent, type PointerEvent } from "react";
import { Link } from "react-router-dom";
import { PERGUNTAS, quandoLegivel, type AvaliacaoDaLista, type CampoDaNota, type Origem } from "../lib/avaliacoes";
import { horaComArtigo } from "../lib/horaNoTexto";
import { SeloDaMedia } from "./AdminAvaliacoesUi";

/**
 * O detalhe de UMA avaliação (000328): as três notas, a média, o comentário
 * inteiro, quando e de onde veio — e nunca quem escreveu. O hub não guarda o
 * aluno das avaliações que vieram do app antigo, e o das que foram dadas aqui
 * não sai das leituras da equipe (a promessa que o formulário faz antes das
 * notas).
 *
 * `<dialog>` nativo com `showModal()`, pela razão do `ModalVigencias`: o Esc, a
 * armadilha de foco e o fundo inerte vêm do navegador. E as quatro regras que
 * a casa já pagou:
 *   · `m-auto` — o reset zera a margem, e sem ela o diálogo abre no canto (a
 *     cicatriz do Mapa de Concursos);
 *   · o `<dialog>` é UM só e fica sempre no DOM; trocar o elemento a cada
 *     abertura deixava o efeito segurando um nó que já tinha saído da página;
 *   · nenhuma classe de `display` no próprio `<dialog>`: `flex` venceria o
 *     `display: none` que o navegador dá ao diálogo FECHADO, e ele apareceria
 *     solto no meio da página. O desenho mora na caixa de dentro;
 *   · `max-h` na caixa e rolagem só no MEIO: o título e o rodapé ficam presos, e
 *     num comentário de mil caracteres o "Fechar" não sai do alcance.
 */
export type PropsDoDialogo = {
  /** Aberto quando não é nulo. */
  avaliacao: AvaliacaoDaLista | null;
  aoFechar: () => void;
  /** O link para a página da ferramenta, montado por quem chama (ele sabe o período). */
  linkDaFerramenta: (slug: string) => string;
};

const ROTULO_DA_NOTA: Record<CampoDaNota, string> = {
  utilidade: "Utilidade",
  facilidade: "Facilidade",
  satisfacao: "Satisfação",
};

const ORIGEM_POR_EXTENSO: Record<Origem, string> = {
  hub: "do hub",
  central_feedbacks: "do app antigo, a Central Feedbacks",
};

/**
 * Por quanto tempo, depois de abrir, o diálogo ignora os cliques. É a folga de
 * um clique duplo: o primeiro abre, e o segundo chega logo depois (ver
 * `aoCapturarClique`).
 */
const CLIQUES_IGNORADOS_AO_ABRIR_MS = 300;

/**
 * Por quanto tempo, depois de FECHAR, a página ignora o segundo clique de um
 * clique duplo (ver `engolirOSegundoClique`). É maior que o tempo de clique
 * duplo do sistema — o navegador conta como duplo (`detail` 2) dois cliques até
 * esse tempo, que no Windows é de 500 ms por padrão —, senão o clique duplo
 * lento escaparia. Como só o segundo clique de um duplo é engolido, a folga não
 * custa nada ao clique simples.
 */
const CLIQUES_DUPLOS_IGNORADOS_AO_FECHAR_MS = 600;

/**
 * O espelho da guarda de abrir (`aoCapturarClique`), achado na revisão da tela
 * de 30/09. Um clique duplo no «Fechar», no × ou no fundo fecha o diálogo no
 * PRIMEIRO clique, e `close()` tira o diálogo da camada do alto na hora. O
 * segundo clique então cai na PÁGINA, no que estiver sob o ponteiro: o botão
 * «dia e hora» de outra linha, que reabre o diálogo com OUTRA avaliação; um
 * link, que navega; «Próximas →», que troca a página.
 *
 * Por isso, logo antes de fechar pelo clique, a página passa a engolir por
 * `CLIQUES_DUPLOS_IGNORADOS_AO_FECHAR_MS`, na CAPTURA do `document` — antes da
 * raiz do React e antes da ação de qualquer link —, o clique que é o segundo de
 * uma sequência (`detail > 1`). Só esse: um clique simples logo depois de fechar
 * continua valendo, e o teclado (`detail` 0) nunca é engolido.
 */
function engolirOSegundoClique() {
  const engolir = (e: globalThis.MouseEvent) => {
    if (e.detail > 1) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  document.addEventListener("click", engolir, true);
  window.setTimeout(() => document.removeEventListener("click", engolir, true), CLIQUES_DUPLOS_IGNORADOS_AO_FECHAR_MS);
}

/**
 * Quem abriu o diálogo, para o foco voltar lá quando ele fechar.
 *
 * O botão da linha, quando foi ele (o teclado, ou o clique no Chrome, que foca
 * o botão). O clique na LINHA não foca nada — `<tr>` não é focável, e o Safari
 * nem foca o botão clicado —, e aí o foco voltaria para onde estava antes: o
 * campo de busca, lá no alto da lista. Nesse caso o destino é o botão DAQUELA
 * linha, que as duas listas que abrem o diálogo marcam com `data-avaliacao=<seq>`
 * (é o contrato delas, na especificação da 000328).
 */
function quemAbriu(seq: number): HTMLElement | null {
  const ativo = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
  const linha = document.querySelector<HTMLElement>(`[data-avaliacao="${seq}"]`);
  if (linha && ativo && linha.contains(ativo)) return ativo;
  return linha?.querySelector<HTMLElement>("button") ?? ativo;
}

export default function DialogoDaAvaliacao({ avaliacao, aoFechar, linkDaFerramenta }: PropsDoDialogo) {
  const ref = useRef<HTMLDialogElement>(null);
  const idTitulo = useId();
  const abridor = useRef<HTMLElement | null>(null);
  const apertouNoFundo = useRef(false);
  // Quando o diálogo abriu, no relógio da página (`performance.now()`).
  const abertoEm = useRef(-Infinity);

  // O `close` do diálogo chega DEPOIS (é uma tarefa do navegador): ele lê a
  // avaliação e o `aoFechar` mais recentes, e não os do render em que foi
  // registrado.
  const atual = useRef(avaliacao);
  const aoFecharAtual = useRef(aoFechar);
  useLayoutEffect(() => {
    atual.current = avaliacao;
    aoFecharAtual.current = aoFechar;
  });

  const seq = avaliacao?.seq ?? null;
  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (seq !== null && !dlg.open) {
      abridor.current = quemAbriu(seq);
      dlg.showModal();
      abertoEm.current = performance.now();
    } else if (seq === null && dlg.open) {
      // Quem chama fechou por conta própria (a lista mudou): fecha o diálogo, e
      // o `close` abaixo devolve o foco sem chamar `aoFechar` de novo.
      dlg.close();
    }
  }, [seq]);

  // `fechar` é o caminho de TODO fechamento por clique (o «Fechar», o × e o
  // fundo). O Esc e o fechamento pedido por quem chama não passam por aqui, e
  // não precisam: nenhum dos dois é um clique cujo segundo possa cair na página.
  function fechar() {
    engolirOSegundoClique();
    ref.current?.close();
  }

  function aoFecharODialogo() {
    const alvo = abridor.current;
    abridor.current = null;
    if (alvo?.isConnected) alvo.focus({ preventScroll: true });
    if (atual.current !== null) aoFecharAtual.current();
  }

  // O clique FORA fecha — mas só o que começou fora. Quem seleciona um trecho
  // do comentário para copiar e solta o botão do mouse no fundo gera um clique
  // cujo alvo é o próprio `<dialog>`, e perder a leitura por isso é o pior
  // momento para perdê-la.
  function aoApertar(e: PointerEvent<HTMLDialogElement>) {
    apertouNoFundo.current = e.target === e.currentTarget;
  }
  function aoClicar(e: MouseEvent<HTMLDialogElement>) {
    if (e.target === e.currentTarget && apertouNoFundo.current) fechar();
    apertouNoFundo.current = false;
  }

  /**
   * O clique duplo numa linha abre o diálogo no PRIMEIRO clique — o React aplica
   * o estado e roda o efeito que chama `showModal()` antes de o segundo chegar,
   * e o diálogo já está na camada do alto. O segundo clique então cai no que
   * estiver naquele ponto: no fundo, e o diálogo fecha no mesmo instante em que
   * abriu; ou num botão ou link de dentro («Fechar», «Ver a ferramenta»), e ele
   * fecha ou navega sem ninguém ter pedido.
   *
   * Por isso, na fase de CAPTURA (antes de qualquer botão de dentro e do
   * `aoClicar` do fundo), o diálogo engole o clique que é o segundo de uma
   * sequência (`detail > 1`) e todo clique dos primeiros
   * `CLIQUES_IGNORADOS_AO_ABRIR_MS` depois de abrir. As duas regras, e não uma:
   * o `detail` pega o duplo clique que o navegador contou como duplo, e o
   * relógio pega o segundo clique rápido que chega SEM essa marca — a contagem
   * depende do navegador e do dispositivo (no toque, por exemplo, e quando o
   * ponteiro anda entre os dois cliques). O clique pelo teclado tem `detail` 0 e
   * só cai na regra do relógio. O Esc não é clique, e continua fechando na hora.
   */
  function aoCapturarClique(e: MouseEvent<HTMLDialogElement>) {
    if (e.detail > 1 || performance.now() - abertoEm.current < CLIQUES_IGNORADOS_AO_ABRIR_MS) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  return (
    <dialog
      ref={ref}
      onClose={aoFecharODialogo}
      onPointerDown={aoApertar}
      onClickCapture={aoCapturarClique}
      onClick={aoClicar}
      aria-labelledby={avaliacao ? idTitulo : undefined}
      data-dialogo-da-avaliacao={avaliacao?.seq}
      className="m-auto w-[min(94vw,38rem)] rounded-cf-lg bg-superficie p-0 text-tinta shadow-2xl backdrop:bg-azul/70 backdrop:backdrop-blur-sm"
    >
      {avaliacao && (
        <Conteudo avaliacao={avaliacao} idTitulo={idTitulo} link={linkDaFerramenta(avaliacao.card)} fechar={fechar} />
      )}
    </dialog>
  );
}

function Conteudo({
  avaliacao: a,
  idTitulo,
  link,
  fechar,
}: {
  avaliacao: AvaliacaoDaLista;
  idTitulo: string;
  link: string;
  fechar: () => void;
}) {
  const quando = quandoLegivel(a.em);
  return (
    <div className="flex max-h-[min(90dvh,46rem)] flex-col">
      <div className="flex items-start justify-between gap-4 border-b border-borda px-5 py-4 sm:px-6">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-cinza">Avaliação de aluno</p>
          <h2 id={idTitulo} className="mt-0.5 font-display text-xl font-bold text-titulo wrap-anywhere">
            {a.nome}
          </h2>
          <p className="mt-1 text-sm text-cinza">
            {/* A data é a de quando o aluno deu a avaliação — ou a mudou pela
                última vez: é por ela que o período filtra. */}
            <time dateTime={a.em}>
              {quando.dia}, {horaComArtigo(quando.hora)}
            </time>
            {" · "}
            <span data-dialogo-origem={a.origem}>{ORIGEM_POR_EXTENSO[a.origem]}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={fechar}
          aria-label="Fechar"
          className="grid size-11 shrink-0 place-items-center rounded-full text-cinza transition hover:bg-chip hover:text-titulo"
          data-fechar-dialogo="topo"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="size-5" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
        <div className="flex items-center gap-3">
          <SeloDaMedia media={a.media} grande />
          <p className="text-sm text-cinza">a média das três notas</p>
        </div>

        <ul className="mt-5 space-y-4" aria-label="As três notas">
          {PERGUNTAS.map((p) => {
            const nota = a[p.campo];
            return (
              <li key={p.campo} data-dialogo-nota={p.campo} data-valor={nota}>
                <p className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-semibold text-tinta">{ROTULO_DA_NOTA[p.campo]}</span>
                  <span className="text-sm tabular-nums text-cinza">
                    <strong className="font-display text-base font-bold text-titulo">{nota}</strong> de 10
                  </span>
                </p>
                {/* A barra de 0 a 10: a trilha é o mesmo azul, mais claro, e a
                    parte cheia é a nota. Só para o olho — o número já está escrito. */}
                <div aria-hidden="true" className="mt-1.5 h-2 overflow-hidden rounded-full bg-acento/15">
                  <div className="h-full rounded-full bg-acento" style={{ width: `${Math.min(100, Math.max(0, nota * 10))}%` }} />
                </div>
                {/* A pergunta que o aluno respondeu — a mesma do formulário. */}
                <p className="mt-1 text-xs text-cinza">{p.texto}</p>
              </li>
            );
          })}
        </ul>

        <div className="mt-6">
          <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-cinza">Comentário</h3>
          {a.comentario ? (
            <p
              className="mt-2 whitespace-pre-wrap rounded-xl bg-superficie-2 p-4 text-sm leading-relaxed text-tinta wrap-anywhere"
              data-dialogo-comentario
            >
              {a.comentario}
            </p>
          ) : (
            <p className="mt-2 text-sm italic text-cinza" data-dialogo-comentario data-sem-comentario>
              Sem comentário.
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-borda px-5 py-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          {/* Fecha ANTES de navegar: se a página da ferramenta morasse na mesma
              sub-aba, o diálogo continuaria aberto por cima dela. */}
          <Link to={link} onClick={fechar} className="btn-contorno min-h-11 text-sm" data-ver-ferramenta>
            Ver a ferramenta
            <span aria-hidden="true">→</span>
          </Link>
          <button
            type="button"
            onClick={fechar}
            className="min-h-11 px-2 text-sm font-semibold text-link underline-offset-2 hover:underline"
            data-fechar-dialogo="rodape"
          >
            Fechar
          </button>
        </div>
        <p className="mt-2 text-xs text-cinza">A equipe vê as notas e o comentário, nunca quem escreveu.</p>
      </div>
    </div>
  );
}
